import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Button, Flex, Text } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import { TextInput } from 'react-hook-form-mantine';
import React from 'react';
import { useTemplateDetailsTabController } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/TemplateDetailsTab/useTemplateDetailsTabController.ts';
import { AVAILABLE_VERSION_BY_TEMPLATE_TYPE } from '@dealstack/shared/lib/common/business/generatableDocuments.ts';
import type { GeneratableDocumentWorkflowTemplateType } from '@dealstack/shared/lib/generated';
import { LinkedProjectsTable } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/TemplateDetailsTab/LinkedProjectsTable/LinkedProjectsTable.tsx';

interface Props {
	templateId: string;
	templateName: string;
	templateType: GeneratableDocumentWorkflowTemplateType;
	refetchTemplateData: () => Promise<void>;
}

export const TemplateDetailsTab = ({
	templateId,
	templateName,
	templateType,
	refetchTemplateData,
}: Props) => {
	const { editTemplateDetailsForm, onSubmit, isUpdating, isEditMode, setIsEditMode } =
		useTemplateDetailsTabController(templateName, templateId, refetchTemplateData);

	return (
		<Flex direction="column" gap="3rem">
			<ContainerWithHeader
				icon={<Icon variant="document-v3" size="xl" />}
				title={
					<Flex gap="1rem" align="center" justify="space-between" wrap="nowrap" w="100%">
						<Text size="B6Bold">Template details</Text>

						{isEditMode ? (
							<Flex gap="1rem">
								<Button
									variant="outline"
									onClick={() => setIsEditMode(false)}
									disabled={isUpdating}
								>
									Cancel
								</Button>

								<Button
									variant="primary"
									onClick={editTemplateDetailsForm.handleSubmit(onSubmit)}
									disabled={isUpdating}
								>
									Save
								</Button>
							</Flex>
						) : (
							<Button variant="primary" onClick={() => setIsEditMode(true)}>
								Edit
							</Button>
						)}
					</Flex>
				}
			>
				<Flex gap="2rem" justify="space-between">
					<FormProvider {...editTemplateDetailsForm}>
						{isEditMode ? (
							<TextInput
								name="name"
								control={editTemplateDetailsForm.control}
								label="Name"
								placeholder="Enter template name"
								size="md"
								w="33%"
							/>
						) : (
							<Flex direction="column" gap="0.5rem" w="33%">
								<Text size="B6Bold">Name:</Text>
								<Text>{templateName}</Text>
							</Flex>
						)}

						<Flex direction="column" gap="0.5rem" w="33%">
							<Text size="B6Bold">Type:</Text>
							<Text>{templateType}</Text>
						</Flex>

						<Flex direction="column" gap="0.5rem" w="33%">
							<Text size="B6Bold">Versions:</Text>
							<Text>
								{AVAILABLE_VERSION_BY_TEMPLATE_TYPE[templateType]?.length > 1
									? 'This template have 2 different versions: one for INDIVIDUAL and another for PIV entities'
									: 'This template have same version for INDIVIDUAL and for PIV entities'}
							</Text>
						</Flex>
					</FormProvider>
				</Flex>
			</ContainerWithHeader>

			<LinkedProjectsTable templateId={templateId} />
		</Flex>
	);
};
